// src/admin/components/payments/SubscriptionPaymentList.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RiMoneyDollarCircleLine, RiCheckLine, RiCloseLine, RiSearchLine, RiFilter3Line } from 'react-icons/ri';
import { subscriptionPaymentsAPI } from '../../../services/api';
import { useTheme } from '../../../contexts/ThemeContext';

import { getCurrentUser } from '../../../models/authModel';

const SubscriptionPaymentList = () => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();

    const [payments, setPayments] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [managing, setManaging] = useState(false);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all'); // all, pending, completed, failed

    const user = getCurrentUser();
    const isSuperAdmin = user?.role === 'super_admin';

    useEffect(() => {
        fetchData();
    }, [filter]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch payments
            const filters = {};
            if (filter !== 'all') filters.status = filter;

            let paymentsResponse;
            if (isSuperAdmin) {
                paymentsResponse = await subscriptionPaymentsAPI.getAll(filters);
            } else {
                paymentsResponse = await subscriptionPaymentsAPI.getHistory();
            }

            if (paymentsResponse.success) {
                setPayments(paymentsResponse.payments);
            } else {
                setError('Failed to fetch payments');
            }

            // Fetch current subscription status (only for non-super admins or relevant context)
            if (!isSuperAdmin) {
                const statusResponse = await subscriptionPaymentsAPI.getStatus();
                if (statusResponse.success) {
                    setSubscription(statusResponse);
                }
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Error loading data');
        } finally {
            setLoading(false);
        }
    };

    const handleManageSubscription = async () => {
        setManaging(true);
        try {
            const response = await subscriptionPaymentsAPI.createPortalSession();
            if (response.success && response.url) {
                window.location.href = response.url;
            } else {
                alert('Failed to redirect to subscription management');
            }
        } catch (err) {
            console.error('Error creating portal session:', err);
            alert('Failed to open subscription management. Please try again.');
        } finally {
            setManaging(false);
        }
    };

    const handleApprove = async (id) => {
        if (!isSuperAdmin) return;
        if (!window.confirm('Are you sure you want to approve this payment?')) return;

        try {
            await subscriptionPaymentsAPI.approve(id);
            fetchData(); // Refresh list
            alert('Payment approved successfully');
        } catch (err) {
            console.error('Error approving payment:', err);
            alert('Failed to approve payment');
        }
    };

    const handleReject = async (id) => {
        if (!isSuperAdmin) return;
        const reason = window.prompt('Enter rejection reason:');
        if (!reason) return;

        try {
            await subscriptionPaymentsAPI.reject(id, reason);
            fetchData(); // Refresh list
            alert('Payment rejected');
        } catch (err) {
            console.error('Error rejecting payment:', err);
            alert('Failed to reject payment');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'text-green-600 bg-green-100';
            case 'pending': return 'text-yellow-600 bg-yellow-100';
            case 'failed': return 'text-red-600 bg-red-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    return (
        <div className={`p-6 ${isDarkMode ? 'bg-zinc-900 text-white' : 'bg-gray-50 text-gray-900'} min-h-screen`}>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center">
                        <RiMoneyDollarCircleLine className="mr-2" />
                        {t('billing_subscription') || 'Billing & Subscription'}
                    </h1>
                    <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Manage your subscription and view payment history
                    </p>
                </div>
            </div>

            {/* Subscription Status Card — hidden for custom1 (SUBSCRIPTION_BYPASS) */}


            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t('payment_history') || 'Payment History'}</h3>
                <div className="flex items-center space-x-4">
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className={`p-2 rounded border ${isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300'}`}
                    >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                    </select>
                </div>
            </div>

            {
                loading ? (
                    <div className="text-center py-10">Loading payments...</div>
                ) : error ? (
                    <div className="text-red-500 text-center py-10">{error}</div>
                ) : payments.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">No payment history found.</div>
                ) : (
                    <div className={`overflow-x-auto rounded-lg shadow ${isDarkMode ? 'bg-zinc-800' : 'bg-white'}`}>
                        <table className="w-full text-start">
                            <thead className={`text-xs uppercase ${isDarkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-50 text-gray-500'}`}>
                                <tr>
                                    <th className="px-6 py-3">Date</th>
                                    <th className="px-6 py-3">Organization</th>
                                    <th className="px-6 py-3">Amount</th>
                                    <th className="px-6 py-3">Method</th>
                                    <th className="px-6 py-3">Status</th>
                                    {isSuperAdmin && <th className="px-6 py-3">Actions</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {payments.map((payment) => (
                                    <tr key={payment.id} className={isDarkMode ? 'border-zinc-700' : ''}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {new Date(payment.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="font-medium">{payment.organization_name || 'My Organization'}</div>
                                            <div className="text-xs text-gray-500">{payment.organization_slug}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap font-medium">
                                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: payment.currency || 'USD' }).format(payment.amount)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap capitalize">
                                            {payment.payment_method}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs rounded-full font-semibold ${getStatusColor(payment.status)}`}>
                                                {payment.status}
                                            </span>
                                        </td>
                                        {isSuperAdmin && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {payment.status === 'pending' && (
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => handleApprove(payment.id)}
                                                            className="text-green-600 hover:text-green-800"
                                                            title="Approve"
                                                        >
                                                            <RiCheckLine size={20} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleReject(payment.id)}
                                                            className="text-red-600 hover:text-red-800"
                                                            title="Reject"
                                                        >
                                                            <RiCloseLine size={20} />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            }
        </div >
    );
};
export default SubscriptionPaymentList;
